import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

const MONTHS_NL = [
  'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
  'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December',
];
const DAY_HEADERS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// 0=Mon, 6=Sun
function getFirstWeekday(year, month) {
  const day = new Date(year, month, 1).getDay();
  return (day + 6) % 7;
}

function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  if (!y || !m || !d) return null;
  return { year: y, month: m - 1, day: d };
}

export default function DatePicker({ value, onChange }) {
  const { colors, themeMode } = useTheme();
  const isLight = themeMode === 'light';

  const today = new Date();
  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const parsed = parseDate(value);
  const initYear = parsed?.year ?? today.getFullYear();
  const initMonth = parsed?.month ?? today.getMonth();

  const [viewYear, setViewYear] = useState(initYear);
  const [viewMonth, setViewMonth] = useState(initMonth);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstWeekday = getFirstWeekday(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Build grid cells: nulls for empty leading slots, then day numbers
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const headerColor = colors.text;
  const mutedColor = colors.muted;
  const borderColor = isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)';
  const navBg = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';

  return (
    <div style={{ userSelect: 'none' }}>
      {/* Month/Year header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 14,
      }}>
        <button
          onClick={prevMonth}
          style={{
            width: 32, height: 32,
            borderRadius: 8,
            border: `1px solid ${borderColor}`,
            background: navBg,
            color: mutedColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <ChevronLeft size={16} />
        </button>

        <span style={{
          fontSize: 15,
          fontWeight: 600,
          color: headerColor,
          letterSpacing: '-0.01em',
        }}>
          {MONTHS_NL[viewMonth]} {viewYear}
        </span>

        <button
          onClick={nextMonth}
          style={{
            width: 32, height: 32,
            borderRadius: 8,
            border: `1px solid ${borderColor}`,
            background: navBg,
            color: mutedColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        marginBottom: 6,
      }}>
        {DAY_HEADERS.map(d => (
          <div key={d} style={{
            textAlign: 'center',
            fontSize: 11,
            fontWeight: 600,
            color: mutedColor,
            opacity: 0.5,
            letterSpacing: '0.05em',
            paddingBottom: 4,
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '3px 2px',
      }}>
        {cells.map((day, i) => {
          if (day === null) {
            return <div key={`empty-${i}`} />;
          }

          const dateStr = toDateStr(viewYear, viewMonth, day);
          const isSelected = dateStr === value;
          const isToday = dateStr === todayStr;

          return (
            <div
              key={day}
              onClick={() => onChange(dateStr)}
              style={{
                height: 38,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 9,
                cursor: 'pointer',
                position: 'relative',
                fontSize: 14,
                fontWeight: isSelected ? 700 : isToday ? 600 : 400,
                color: isSelected
                  ? '#fff'
                  : isToday
                    ? '#f97316'
                    : headerColor,
                background: isSelected
                  ? '#f97316'
                  : 'transparent',
                transition: 'background 0.12s, color 0.12s',
              }}
              onMouseEnter={e => {
                if (!isSelected) e.currentTarget.style.background = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)';
              }}
              onMouseLeave={e => {
                if (!isSelected) e.currentTarget.style.background = 'transparent';
              }}
            >
              {day}
              {isToday && !isSelected && (
                <div style={{
                  position: 'absolute',
                  bottom: 3,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  background: '#f97316',
                }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
